import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import * as L from "leaflet";

export interface PuntoElegido {
  latitud: number;
  longitud: number;
}

interface ResultadoBusqueda {
  nombre: string;
  latitud: number;
  longitud: number;
}

/**
 * Un mapa para señalar dónde está un sitio: se busca la dirección, se da
 * clic (o se arrastra el punto) y el círculo muestra el radio de la
 * geocerca. Devuelve latitud y longitud; el radio lo maneja quien lo usa.
 *
 * Mapa de OpenStreetMap y búsqueda con Nominatim: no piden llave ni cobran.
 * La búsqueda va con fetch y no con HttpClient a propósito: los
 * interceptores le pegarían el token de la sesión a una petición a un
 * tercero.
 */
@Component({
  selector: "app-selector-ubicacion",
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: "./selector-ubicacion.component.html",
  styleUrls: ["./selector-ubicacion.component.css"],
})
export class SelectorUbicacionComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() latitud: number | null = null;
  @Input() longitud: number | null = null;
  /** Metros del círculo; con null no se dibuja. */
  @Input() radioMetros: number | null = null;
  @Output() cambio = new EventEmitter<PuntoElegido>();

  @ViewChild("lienzo", { static: true }) lienzo!: ElementRef<HTMLDivElement>;

  busqueda = "";
  buscando = false;
  resultados: ResultadoBusqueda[] = [];
  aviso: string | null = null;

  private mapa: L.Map | null = null;
  private punto: L.CircleMarker | null = null;
  private circulo: L.Circle | null = null;
  private observador: ResizeObserver | null = null;

  /** México entero: es donde arranca el mapa cuando el sitio aún no tiene punto. */
  private static readonly CENTRO_INICIAL: L.LatLngTuple = [23.6345, -102.5528];
  private static readonly ZOOM_INICIAL = 5;
  private static readonly ZOOM_SITIO = 17;

  constructor(
    private zona: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    // Fuera de Angular: Leaflet dispara muchos eventos de ratón y no hace
    // falta una detección de cambios por cada uno.
    this.zona.runOutsideAngular(() => {
      this.mapa = L.map(this.lienzo.nativeElement, {
        center: this.tienePunto ? [this.latitud!, this.longitud!] : SelectorUbicacionComponent.CENTRO_INICIAL,
        zoom: this.tienePunto ? SelectorUbicacionComponent.ZOOM_SITIO : SelectorUbicacionComponent.ZOOM_INICIAL,
        zoomControl: true,
        attributionControl: true,
      });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      }).addTo(this.mapa);
      this.mapa.on("click", (evento: L.LeafletMouseEvent) => this.elegir(evento.latlng.lat, evento.latlng.lng, false));
      this.pintarPunto();
      // El diálogo se anima y crece cuando aparecen resultados: cada vez
      // que el contenedor cambia de tamaño, Leaflet vuelve a medirlo. Sin
      // esto el mapa se dibujaba a medias hasta que alguien lo movía.
      if (typeof ResizeObserver !== "undefined") {
        this.observador = new ResizeObserver(() => this.refrescar());
        this.observador.observe(this.lienzo.nativeElement);
      }
    });
    setTimeout(() => this.refrescar(), 250);
  }

  ngOnChanges(cambios: SimpleChanges): void {
    if (!this.mapa) {
      return;
    }
    if (cambios["latitud"] || cambios["longitud"] || cambios["radioMetros"]) {
      this.zona.runOutsideAngular(() => this.pintarPunto());
    }
  }

  ngOnDestroy(): void {
    this.observador?.disconnect();
    this.observador = null;
    this.mapa?.remove();
    this.mapa = null;
  }

  /** Vuelve a medir el contenedor; llamar cuando el mapa se muestra tras estar oculto. */
  refrescar(): void {
    this.mapa?.invalidateSize();
  }

  get tienePunto(): boolean {
    return this.latitud != null && this.longitud != null && !Number.isNaN(Number(this.latitud)) && !Number.isNaN(Number(this.longitud));
  }

  async buscar(): Promise<void> {
    const texto = this.busqueda.trim();
    if (texto.length < 3) {
      this.aviso = "Escriba al menos tres letras.";
      return;
    }
    this.buscando = true;
    this.aviso = null;
    this.resultados = [];
    try {
      const url = "https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&addressdetails=0&countrycodes=mx&q=" + encodeURIComponent(texto);
      const respuesta = await fetch(url, { headers: { Accept: "application/json", "Accept-Language": "es" } });
      const datos = respuesta.ok ? await respuesta.json() : [];
      this.resultados = (Array.isArray(datos) ? datos : []).map((d: any) => ({
        nombre: String(d.display_name || ""),
        latitud: Number(d.lat),
        longitud: Number(d.lon),
      }));
      if (this.resultados.length === 0) {
        this.aviso = "No se encontró. Pruebe con la colonia o el municipio, o dé clic en el mapa.";
      }
    } catch {
      this.aviso = "No se pudo consultar el buscador de direcciones. Dé clic en el mapa para señalar el sitio.";
    } finally {
      this.buscando = false;
      this.cdr.detectChanges();
    }
  }

  irA(resultado: ResultadoBusqueda): void {
    this.resultados = [];
    this.busqueda = resultado.nombre;
    this.elegir(resultado.latitud, resultado.longitud, true);
  }

  usarMiUbicacion(): void {
    if (!navigator.geolocation) {
      this.aviso = "Este navegador no comparte la ubicación.";
      return;
    }
    this.aviso = null;
    navigator.geolocation.getCurrentPosition(
      (posicion) => this.zona.run(() => this.elegir(posicion.coords.latitude, posicion.coords.longitude, true)),
      () => this.zona.run(() => {
        this.aviso = "No se pudo leer su ubicación. Permita el acceso o señale el sitio en el mapa.";
        this.cdr.detectChanges();
      }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  }

  // ------------------------------------------------------------------

  private elegir(lat: number, lng: number, centrar: boolean): void {
    this.latitud = Number(lat.toFixed(7));
    this.longitud = Number(lng.toFixed(7));
    this.zona.runOutsideAngular(() => {
      this.pintarPunto();
      if (centrar && this.mapa) {
        this.mapa.setView([this.latitud!, this.longitud!], Math.max(this.mapa.getZoom(), SelectorUbicacionComponent.ZOOM_SITIO));
      }
    });
    this.zona.run(() => {
      this.cambio.emit({ latitud: this.latitud!, longitud: this.longitud! });
      this.cdr.detectChanges();
    });
  }

  private pintarPunto(): void {
    if (!this.mapa) {
      return;
    }
    if (!this.tienePunto) {
      this.punto?.remove();
      this.circulo?.remove();
      this.punto = null;
      this.circulo = null;
      return;
    }
    const centro: L.LatLngTuple = [Number(this.latitud), Number(this.longitud)];
    if (!this.punto) {
      this.punto = L.circleMarker(centro, {
        radius: 8,
        color: "#ffffff",
        weight: 2,
        fillColor: "#b71c1c",
        fillOpacity: 1,
      }).addTo(this.mapa);
    } else {
      this.punto.setLatLng(centro);
    }
    const radio = this.radioMetros != null && Number(this.radioMetros) > 0 ? Number(this.radioMetros) : null;
    if (radio == null) {
      this.circulo?.remove();
      this.circulo = null;
      return;
    }
    if (!this.circulo) {
      this.circulo = L.circle(centro, {
        radius: radio,
        color: "#b71c1c",
        weight: 1.5,
        fillColor: "#b71c1c",
        fillOpacity: 0.12,
      }).addTo(this.mapa);
    } else {
      this.circulo.setLatLng(centro);
      this.circulo.setRadius(radio);
    }
  }
}
